import React, { useCallback, useMemo, useState } from "react";

import { selectNoiseReduction } from "../../helpers";
import { Dispatch } from "../../StateManager/state";
import { PreviewImageLoader } from "./PreviewImageLoader";

type Props = Readonly<{
  activePage: number;
  dispatch: Dispatch;
  highConfidenceThreshold: number;
  pageConfidences: Array<number>;
  pageImageURIs: ReadonlyArray<string>;
  showAll: boolean;
}>;

export const PageList = ({
  activePage,
  dispatch,
  highConfidenceThreshold,
  pageConfidences,
  pageImageURIs,
  showAll,
}: Props) => {
  const [scrollRef, setScrollRef] = useState<HTMLElement | null>(null);
  const imageLoadCallbacks = useMemo(
    () =>
      pageImageURIs.map((_, index) => {
        return (image: HTMLImageElement) => {
          dispatch({
            image,
            index,
            noiseReduction: selectNoiseReduction(image),
            type: "SET NOISE REDUCTION LEVEL",
          });
        };
      }),
    [dispatch, pageImageURIs],
  );
  const onFocusCallbacks = useMemo(
    () =>
      pageImageURIs.map((_, index) => {
        return () => {
          dispatch({
            page: index,
            type: "SELECT PAGE",
          });
        };
      }),
    [dispatch, pageImageURIs],
  );
  let contents = null;
  if (scrollRef) {
    contents = pageImageURIs.map((uri, index) => (
      <PreviewImageLoader
        active={activePage === index}
        index={index}
        key={uri}
        onFocus={onFocusCallbacks[index]}
        onImageLoaded={imageLoadCallbacks[index]}
        scrollParent={scrollRef}
        src={uri}
        total={pageImageURIs.length}
        visible={showAll || pageConfidences[index] <= highConfidenceThreshold}
      />
    ));
  }
  const setRefCallback = useCallback(
    (ref: HTMLElement | null) => {
      if (ref) {
        setScrollRef(ref);
      }
    },
    [setScrollRef],
  );
  return (
    <div className="page-list" ref={setRefCallback}>
      {contents}
    </div>
  );
};
PageList.displayName = "PageList";
